/**
 * Identity for scoping Review cache to the open Word document.
 *
 * Uses basename (same source as "Show suggested projects") plus timestamps
 * stored inside the docx — never the local file URL/path, which differs
 * across machines and folders.
 *
 * Primary key: name + lastSaveTime.
 * Fallback when lastSaveTime is missing (unsaved): name + createdAt.
 */

export type DocumentIdentity = {
  name: string;
  lastSaveTime: string | null;
  createdAt: string | null;
};

let currentIdentity: DocumentIdentity | null = null;

export function getCurrentDocumentIdentity(): DocumentIdentity | null {
  return currentIdentity;
}

export function setCurrentDocumentIdentity(identity: DocumentIdentity | null): void {
  currentIdentity = identity;
}

export function normalizeDocumentName(name: string): string {
  return (name || '').trim().toLowerCase();
}

/**
 * Stable map key, or null when identity is too weak to persist
 * (no lastSaveTime and no createdAt).
 */
export function documentIdentityKey(identity: DocumentIdentity): string | null {
  const name = normalizeDocumentName(identity.name);
  if (!name) return null;
  if (identity.lastSaveTime) {
    return `${name}|saved:${identity.lastSaveTime}`;
  }
  if (identity.createdAt) {
    return `${name}|created:${identity.createdAt}`;
  }
  return null;
}

export function documentIdentitiesMatch(
  stored: DocumentIdentity | null | undefined,
  current: DocumentIdentity | null | undefined,
): boolean {
  if (!stored || !current) return false;
  const a = documentIdentityKey(stored);
  const b = documentIdentityKey(current);
  return a !== null && a === b;
}
